import { CreateButton, Panel, Select, Spinner, TabsPanel } from '@ixicare/ui';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import connect from '../../../store/connect';
import { State } from '~@types/store';
import { useSelectedSite } from '~/hooks/useSelectedSite';
import { t } from '~i18n';
import { DeviceType, ProfileType } from '@ixicare/resources';
import { BeaconsTable } from '~components/Tables/DeviceTables/Beacons/BeaconsTable';
import { DeviceFinderTable } from '~components/Tables/DeviceTables/DeviceFinderTable';
import { GatewaysTable } from '~components/Tables/DeviceTables/Gateways/GatewaysTable';
import { beaconTableConfig } from '~components/Tables/DeviceTables/Beacons/tableConfig';
import {
  wristwatchTableConfig,
  wristbandTableConfig,
  buttonTableConfig,
  eviewTableConfig,
  twigTableConfig,
  apiTableConfig,
} from '~components/Tables/DeviceTables/Devices/tableConfig';
import { gatewaysTableConfig } from '~components/Tables/DeviceTables/Gateways/tableConfig';
import { DevicesTable } from '~components/Tables/DeviceTables/Devices/DevicesTable';
import AuthStore from '~store/Auth';
import { CreateDeviceModal } from '../components/DeviceModals/CreateDeviceModal';
import { IXI_LITE_TYPES, WRISTBAND_TYPES } from '../utils/constants';
import { hasSiteConfig, siteConfigGroup } from '../utils/siteConfigGroups';
import SiteConfigButton from '../components/SiteConfigButton';

// Stable empty selection so unselected tabs don't get a fresh Set each render.
const EMPTY_SELECTION: Set<string> = new Set();

enum ETabs {
  wristwatches = 'wristwatches',
  wristbands = 'wristbands',
  buttons = 'buttons',
  eviews = 'eviews',
  twigs = 'twigs',
  beacons = 'beacons',
  gateways = 'gateways',
  api = 'api',
  deviceFinder = 'deviceFinder',
}

// Maps the active (sub)tab to the device type the create modal should default to.
// Tabs not present here (Beacons/Gateways/Device Finder) hide the create button.
const tabToDeviceType: Record<string, DeviceType> = {
  [ETabs.wristwatches]: DeviceType.IXI_PRO,
  [ETabs.wristbands]: DeviceType.IXI_LITE,
  ALL: DeviceType.IXI_LITE, // wristbands "all" subtab
  [DeviceType.IXI_LITE]: DeviceType.IXI_LITE,
  [DeviceType.IXI_TOUCH]: DeviceType.IXI_TOUCH,
  [DeviceType.IXI_BLACK]: DeviceType.IXI_BLACK,
  [ETabs.buttons]: DeviceType.WALL_MOUNTED_DISTRESS_BUTTON,
  [ETabs.eviews]: DeviceType.EVIEW,
  [ETabs.twigs]: DeviceType.TWIG,
};

interface DeviceOverviewContainerProps {
  loadSites: () => void;
  sitesLoading: boolean;
  authStore: AuthStore;
}

const DeviceOverviewContainer: React.FC<DeviceOverviewContainerProps> = ({ loadSites, sitesLoading, authStore }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    loadSites();
  }, [loadSites]);

  const isInstaller = authStore.userType === ProfileType.Installer;

  const [selectedSite] = useSelectedSite();
  const [deviceTypeOverride, setDeviceTypeOverride] = React.useState<DeviceType[] | undefined>();
  const [currentTab, setCurrentTab] = React.useState<string>(() => window.location.hash.slice(1) || ETabs.wristwatches);

  // Selection lives here (not in the tables) so it survives tab switches, which
  // unmount the inactive tab. Keyed by tableId (== the main-tab id).
  const [selectionByTab, setSelectionByTab] = React.useState<Record<string, Set<string>>>({});
  const isSelectionEmpty = Object.values(selectionByTab).every((set) => set.size === 0);
  const selectionFor = (tableId: string) => selectionByTab[tableId] ?? EMPTY_SELECTION;
  const setSelectionFor = (tableId: string) => (next: Set<string>) =>
    setSelectionByTab((prev) => ({ ...prev, [tableId]: next }));

  // The device types the site-level form would write for the active tab. The wristband tab's
  // "All" covers the whole family; every other tab resolves through its own type's group.
  const siteConfigTypes = React.useMemo(() => {
    if (currentTab === ETabs.wristbands || currentTab === 'ALL') return WRISTBAND_TYPES;
    const deviceType = tabToDeviceType[currentTab];
    return hasSiteConfig(deviceType) ? siteConfigGroup(deviceType) : [];
  }, [currentTab]);

  // Undefined rather than an always-truthy element, so tabs without a configuration form leave
  // the toolbar slot genuinely empty.
  const siteConfigAction = siteConfigTypes.length > 0 ? <SiteConfigButton types={siteConfigTypes} /> : undefined;

  const handleTabChange = (tabId: string) => {
    setCurrentTab(tabId);
    if (!Object.values(DeviceType).includes(tabId as DeviceType)) {
      setDeviceTypeOverride(undefined);
      return;
    }
    const override = tabId === DeviceType.IXI_LITE ? IXI_LITE_TYPES : [tabId as DeviceType];
    setDeviceTypeOverride(override);
  };

  // Read archived state from URL, default to "unarchived"
  const [archiveFilter, setArchiveFilterState] = React.useState<'unarchived' | 'archived'>(
    searchParams.get('archived') === 'true' ? 'archived' : 'unarchived'
  );

  const setArchiveFilter = (value: 'unarchived' | 'archived') => {
    setArchiveFilterState(value);
    const currentHash = window.location.hash;
    const newParams = new URLSearchParams(searchParams);
    if (value === 'archived') {
      newParams.set('archived', 'true');
    } else {
      newParams.delete('archived');
    }
    setSearchParams(newParams, { replace: true });
    // Restore hash after setSearchParams clears it
    if (currentHash) {
      window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString() + currentHash);
    }
  };

  const isArchived = archiveFilter === 'archived';

  // A different site or archive filter is a different dataset, so old selection
  // keys no longer apply — drop them. (The TabsPanel key already remounts here.)
  React.useEffect(() => {
    setSelectionByTab({});
  }, [selectedSite?.id, isArchived]);

  const archiveOptions = React.useMemo(
    () => [
      { label: t('devices.table.isUnarchived'), value: 'unarchived' as const },
      { label: t('devices.table.isArchived'), value: 'archived' as const },
    ],
    []
  );

  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const createDeviceType = tabToDeviceType[currentTab];

  if (sitesLoading) {
    return <Spinner />;
  }

  return (
    <Panel>
      {showCreateModal && selectedSite && createDeviceType && (
        <CreateDeviceModal
          isInstaller={isInstaller}
          siteId={selectedSite.id}
          initialDeviceType={createDeviceType}
          onClose={() => setShowCreateModal(false)}
          onRefresh={() => queryClient.invalidateQueries()}
        />
      )}
      <div className="row mb-15">
        <div className="col-6 col-md-6 col-lg-4">
          <Select
            label={t('devices.table.select')}
            options={archiveOptions}
            value={archiveFilter}
            onChange={setArchiveFilter}
          />
        </div>
      </div>
      <TabsPanel
        key={`${selectedSite?.id ?? 'no-site'}-${isArchived}`}
        useHash={true}
        onChange={handleTabChange}
        tabs={[
          {
            id: ETabs.wristwatches,
            label: t('device-overview.tabs.wristwatches'),
            content: (
              <DevicesTable
                site={selectedSite}
                isArchived={isArchived}
                toolbarActions={siteConfigAction}
                config={wristwatchTableConfig}
                selectedKeys={selectionFor(wristwatchTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(wristwatchTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.wristbands,
            label: t('device-overview.tabs.wristbands'),
            content: (
              <DevicesTable
                site={selectedSite}
                isArchived={isArchived}
                toolbarActions={siteConfigAction}
                config={wristbandTableConfig}
                deviceTypeOverride={deviceTypeOverride}
                selectedKeys={selectionFor(wristbandTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(wristbandTableConfig.tableId)}
              />
            ),
            subItems: [
              {
                id: 'ALL',
                label: t('device-overview.tabs.wristbands'),
              },
              {
                id: DeviceType.IXI_LITE,
                label: t('device.types.ixi_lite'),
              },
              {
                id: DeviceType.IXI_TOUCH,
                label: t('device.types.ixi_touch'),
              },
              {
                id: DeviceType.IXI_BLACK,
                label: t('device.types.ixi_black'),
              },
            ],
          },
          {
            id: ETabs.buttons,
            label: t('device-overview.tabs.buttons'),
            content: (
              <DevicesTable
                site={selectedSite}
                isArchived={isArchived}
                toolbarActions={siteConfigAction}
                config={buttonTableConfig}
                selectedKeys={selectionFor(buttonTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(buttonTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.beacons,
            label: t('device-overview.tabs.beacons'),
            content: (
              <BeaconsTable
                site={selectedSite}
                isArchived={isArchived}
                config={beaconTableConfig}
                selectedKeys={selectionFor(beaconTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(beaconTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.gateways,
            label: t('device-overview.tabs.gateways'),
            content: (
              <GatewaysTable
                site={selectedSite}
                isArchived={isArchived}
                config={gatewaysTableConfig}
                selectedKeys={selectionFor(gatewaysTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(gatewaysTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.eviews,
            label: t('device-overview.tabs.eview'),
            content: (
              <DevicesTable
                site={selectedSite}
                isArchived={isArchived}
                toolbarActions={siteConfigAction}
                config={eviewTableConfig}
                selectedKeys={selectionFor(eviewTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(eviewTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.twigs,
            label: t('device-overview.tabs.twig'),
            content: (
              <DevicesTable
                site={selectedSite}
                isArchived={isArchived}
                toolbarActions={siteConfigAction}
                config={twigTableConfig}
                selectedKeys={selectionFor(twigTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(twigTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.api,
            label: t('device-overview.tabs.api'),
            content: (
              <DevicesTable
                site={selectedSite}
                isArchived={isArchived}
                toolbarActions={siteConfigAction}
                config={apiTableConfig}
                selectedKeys={selectionFor(apiTableConfig.tableId)}
                onSelectedKeysChange={setSelectionFor(apiTableConfig.tableId)}
              />
            ),
          },
          {
            id: ETabs.deviceFinder,
            label: t('device-overview.tabs.deviceFinder'),
            content: <DeviceFinderTable siteId={selectedSite?.id} />,
          },
        ]}
      />
      {selectedSite && createDeviceType && isSelectionEmpty && (
        <CreateButton atBottom onClick={() => setShowCreateModal(true)} />
      )}
    </Panel>
  );
};

export default connect((state: State) => ({
  loadSites: state.sites.loadSites,
  sitesLoading: state.sites.loading,
  authStore: state.auth,
}))(DeviceOverviewContainer);
